import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  Menu, MapPin, CalendarPlus, ChevronDown, Phone, MessageSquare,
  Gift, Heart, Car, Accessibility, CloudRain, Hotel, Clock, Shirt, Camera,
} from "lucide-react";
import { api } from "@/lib/api";
import { HERO_IMAGE, STORY_IMAGE, CTA_IMAGE, BANNER1_IMAGE, BANNER2_IMAGE, downloadICS, APPLE_MAPS_URL, GOOGLE_MAPS_URL } from "@/lib/wedding";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "The Day", href: "#the-day" },
  { label: "Guest Information", href: "#guest-info" },
  { label: "Registry", href: "#registry" },
  { label: "Contact", href: "#contact" },
];

const HERO_BROWN = "#B98A5E";

function Reveal({ children, delay = 0, className = "", y = 34 }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y, filter: "blur(6px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function GoldRule({ className = "" }) {
  const reduce = useReducedMotion();
  return (
    <motion.span
      className={`block h-px bg-gold/50 ${className}`}
      initial={reduce ? false : { scaleX: 0 }}
      whileInView={reduce ? false : { scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      style={{ transformOrigin: "left" }}
    />
  );
}

function Label({ children }) {
  return <span className="text-[11px] sm:text-xs font-sans tracking-luxe uppercase text-gold">{children}</span>;
}

export default function PublicSite() {
  const [c, setC] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    api.get("/site/content").then((r) => setC(r.data)).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!c) {
    return (
      <div className="min-h-screen bg-espresso-dark flex items-center justify-center">
        <motion.p
          className="font-display text-4xl text-cream/80 tracking-wide"
          initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0.6, 1] }} transition={{ duration: 2, repeat: Infinity }}
        >
          A &amp; G
        </motion.p>
      </div>
    );
  }

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <div className="bg-espresso text-ivory font-sans w-full overflow-x-hidden" data-testid="public-site">
      {/* Nav */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          scrolled ? "bg-espresso-dark/92 backdrop-blur-md border-b border-gold-soft py-3" : "bg-transparent py-5"
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative flex items-center justify-center">
          <div className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <button
                key={n.href}
                onClick={() => scrollTo(n.href)}
                className="text-[13px] font-sans tracking-[0.14em] uppercase text-ivory/75 hover:text-gold transition-colors"
                data-testid={`nav-${n.href.slice(1)}`}
              >
                {n.label}
              </button>
            ))}
          </div>
          <Link
            to="/rsvp"
            className="hidden lg:block absolute right-5 sm:right-8 px-6 py-2 rounded-full border border-gold text-gold text-[13px] tracking-[0.14em] uppercase hover:bg-gold hover:text-espresso-dark transition-colors"
            data-testid="nav-rsvp-button"
          >
            RSVP
          </Link>
          <div className="lg:hidden w-full flex items-center justify-between">
            <Link to="/rsvp" className="px-4 py-1.5 rounded-full border border-gold text-gold text-xs tracking-[0.14em] uppercase" data-testid="nav-rsvp-button-mobile">RSVP</Link>
            <Sheet>
              <SheetTrigger asChild>
                <button className="text-ivory p-1" data-testid="nav-menu-button" aria-label="Open menu"><Menu className="w-6 h-6" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-espresso-dark border-gold-soft text-ivory w-72">
                <div className="mt-10 flex flex-col gap-6">
                  {NAV.map((n) => (
                    <SheetTrigger asChild key={n.href}>
                      <button onClick={() => scrollTo(n.href)} className="text-left font-display text-2xl text-ivory hover:text-gold transition-colors" data-testid={`mobile-nav-${n.href.slice(1)}`}>
                        {n.label}
                      </button>
                    </SheetTrigger>
                  ))}
                  <Link to="/rsvp" className="font-display text-2xl text-gold" data-testid="mobile-nav-rsvp">RSVP</Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative h-[100svh] w-full overflow-hidden bg-espresso-dark" data-testid="hero">
        <motion.img
          src={HERO_IMAGE}
          alt="Angeo and Gracie standing together overlooking the coast"
          className="absolute inset-0 w-full h-full object-cover object-[50%_62%] sm:object-center"
          loading="eager"
          initial={reduce ? false : { scale: 1.14 }}
          animate={reduce ? false : { scale: 1 }}
          transition={{ duration: 18, ease: "linear" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso-dark/35 via-espresso-dark/15 to-espresso-dark" />
        <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 200px 60px rgba(20,12,8,0.5)" }} />
        <div className="relative h-full flex flex-col items-center justify-center lg:items-end text-center lg:text-right px-8 lg:pr-20">
          <motion.h1
            className="font-script text-white leading-[0.95] pb-4 text-6xl sm:text-7xl md:text-8xl lg:text-[8rem] lg:-translate-y-4"
            style={{ textShadow: "0 2px 32px rgba(20,12,8,0.5)" }}
            initial={reduce ? false : { opacity: 0, y: 26 }}
            animate={reduce ? false : { opacity: 1, y: 0 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          >
            Angeo &amp; Gracie
          </motion.h1>
        </div>
        <button onClick={() => scrollTo("#welcome")} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" style={{ color: HERO_BROWN }} aria-label="Scroll down" data-testid="scroll-cue">
          <span className="text-[10px] tracking-luxe uppercase">Scroll</span>
          <ChevronDown className="w-5 h-5 animate-scroll-bob" />
        </button>
      </header>

      {/* Welcome */}
      <section id="welcome" className="py-28 sm:py-36 px-6 sm:px-12 bg-espresso" data-testid="welcome-section">
        <Reveal className="max-w-3xl mx-auto text-center">
          <Label>You are invited</Label>
          <div className="flex justify-center my-8"><GoldRule className="w-16" /></div>
          <p className="font-display text-[1.7rem] sm:text-4xl md:text-[2.7rem] leading-[1.35] text-cream font-light">
            {c.welcome_message}
          </p>
        </Reveal>
      </section>

      {/* Banner A */}
      <BannerBand image={STORY_IMAGE} objectPos="center 40%" imgClassName="object-[center_40%] lg:object-[18%_10%] lg:scale-[1.3] lg:origin-[18%_10%]" align="right" testid="banner-1">
        <p className="font-script text-white text-5xl sm:text-7xl leading-[1.05]" style={{ textShadow: "0 2px 26px rgba(20,12,8,0.65)" }}>We're getting married</p>
        <Countdown />
      </BannerBand>

      {/* The Day */}
      <section id="the-day" className="py-28 sm:py-36 px-6 sm:px-12 bg-espresso-dark" data-testid="the-day-section">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-16">
            <Label>The Wedding Day</Label>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl text-ivory font-light">When &amp; Where</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Clock, title: "The Celebration", lines: [c.date_display, `Guests seated for ceremony by ${c.ceremony_time}`, c.setting_description] },
              { icon: MapPin, title: "The Location", lines: [c.location_name, c.address] },
            ].map((card, i) => (
              <Reveal key={card.title} delay={i * 0.12}>
                <div className="h-full rounded-sm border border-gold-soft bg-espresso p-9 sm:p-11 transition-transform duration-500 hover:-translate-y-1">
                  <card.icon className="w-7 h-7 text-gold mb-6" strokeWidth={1.4} />
                  <h3 className="font-display text-2xl text-ivory mb-4 font-normal">{card.title}</h3>
                  {card.lines.map((l) => <p key={l} className="text-cream/85 leading-relaxed">{l}</p>)}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
            <a href={APPLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-gold text-gold hover:bg-gold hover:text-espresso-dark transition-colors tracking-[0.12em] uppercase text-sm" data-testid="apple-maps-btn">
              <MapPin className="w-4 h-4" /> Apple Maps
            </a>
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-gold text-gold hover:bg-gold hover:text-espresso-dark transition-colors tracking-[0.12em] uppercase text-sm" data-testid="google-maps-btn">
              <MapPin className="w-4 h-4" /> Google Maps
            </a>
            <button onClick={() => downloadICS()} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-gold text-espresso-dark hover:bg-ivory transition-colors tracking-[0.12em] uppercase text-sm" data-testid="add-to-calendar-btn">
              <CalendarPlus className="w-4 h-4" /> Add to Calendar
            </button>
          </Reveal>
          {c.schedule_note && (
            <Reveal delay={0.2} className="mt-12 text-center">
              <p className="text-cream/55 italic font-display text-xl">{c.schedule_note}</p>
            </Reveal>
          )}
          <Reveal delay={0.2} className="mt-20 max-w-xl mx-auto text-center">
            <div data-testid="shower-section">
            <GoldRule className="w-16 mx-auto mb-8" />
            <Label>Also Celebrating</Label>
            <h3 className="mt-4 font-display text-3xl text-ivory font-light" data-testid="shower-title">The Wedding Shower</h3>
            <p className="mt-4 text-cream/85 leading-relaxed" data-testid="shower-details">
              Saturday, October 24, 2026 · 10:00 a.m.<br />120 N Muskogee Ave, Tahlequah
            </p>
            <a href="https://www.facebook.com/share/1MCow4PiqU/" target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center justify-center px-6 py-2.5 rounded-full border border-gold text-gold hover:bg-gold hover:text-espresso-dark transition-colors tracking-[0.12em] uppercase text-xs" data-testid="shower-facebook-btn">
              View Shower Details on Facebook
            </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Artsy full-width photo band with Guest Information heading overlay */}
      <section id="guest-info" className="relative h-[70vh] sm:h-[85vh] w-full overflow-hidden bg-espresso-dark" data-testid="story-band">
        <img src={BANNER1_IMAGE} alt="Angeo and Gracie walking together along a garden path" className="absolute inset-0 w-full h-full object-cover object-[center_55%]" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso-dark/85 via-espresso-dark/45 to-espresso/40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 70% 55% at center, rgba(20,12,8,0.55), transparent 75%)" }} />
        <Reveal className="relative h-full flex flex-col items-center justify-center text-center px-8">
          <span className="text-[11px] sm:text-xs font-sans tracking-luxe uppercase text-gold" style={{ textShadow: "0 2px 14px rgba(20,12,8,0.9)" }}>Guest Information</span>
          <h2 className="mt-5 font-display text-5xl sm:text-6xl md:text-7xl text-white font-light" style={{ textShadow: "0 2px 26px rgba(20,12,8,0.85)" }}>Everything you need to know</h2>
        </Reveal>
      </section>

      {/* Guest Information cards */}
      <section className="py-28 sm:py-36 px-6 sm:px-12 bg-espresso-dark" data-testid="guest-info-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <InfoCard icon={Shirt} title={c.dress_code_title} body={c.dress_code_body} testid="info-dress" />
            <InfoCard icon={Car} title="Getting to the Ceremony" body={c.parking_note} testid="info-parking" />
            <InfoCard icon={Accessibility} title="Accessibility" body={c.accessibility_note} testid="info-accessibility" />
            <InfoCard icon={CloudRain} title="Weather & If It Rains" body={`${c.weather_note} ${c.rain_note}`} testid="info-weather" />
          </div>

          {/* Attire palette */}
          <Reveal delay={0.1} className="mt-6">
            <div className="rounded-sm border border-gold-soft bg-espresso p-9">
              <div className="flex items-center gap-3 mb-2">
                <Shirt className="w-6 h-6 text-gold" strokeWidth={1.3} />
                <h3 className="font-display text-2xl text-ivory">A Palette to Inspire You</h3>
              </div>
              <p className="text-cream/70 text-sm mb-8 max-w-xl">
                No need to match exactly. Black and the warm browns (ringed in gold below) are our main colors. We simply love these muted, moody tones and would be delighted if your outfit felt at home among them.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-x-4 gap-y-8">
                {(c.attire_palette || []).map((sw) => {
                  const main = ["Black", "Espresso", "Chocolate", "Dark Terracotta"].includes(sw.name);
                  return (
                    <div key={sw.name} className="flex flex-col items-center gap-3" data-testid={`swatch-${sw.name.split(" ")[0].toLowerCase()}`}>
                      <span
                        className={`w-14 h-14 rounded-full shadow-inner ${main ? "ring-2 ring-gold ring-offset-2 ring-offset-espresso border border-gold" : "border border-gold-soft"}`}
                        style={{ backgroundColor: sw.hex }}
                      />
                      <span className={`text-[10px] tracking-[0.1em] uppercase text-center leading-tight ${main ? "text-gold" : "text-cream/65"}`}>{sw.name}</span>
                    </div>
                  );
                })}
              </div>
              {c.attire_avoid ? (
                <div className="mt-8 pt-6 border-t border-gold-soft/50">
                  <p className="text-[11px] tracking-luxe uppercase text-gold mb-2">Kindly avoid</p>
                  <p className="text-cream/75 text-sm leading-relaxed max-w-xl">{c.attire_avoid}</p>
                </div>
              ) : null}
            </div>
          </Reveal>

          {/* Guests policy */}
          <Reveal delay={0.12} className="mt-6">
            <div className="rounded-sm border border-gold-soft bg-espresso p-9 text-center">
              <Heart className="w-6 h-6 text-gold mx-auto mb-4" strokeWidth={1.3} />
              <h3 className="font-display text-2xl text-ivory mb-3">Guests Named on Your Invitation</h3>
              <p className="text-cream/80 max-w-2xl mx-auto leading-relaxed">{c.children_policy}</p>
            </div>
          </Reveal>

          {/* Accommodations removed */}
        </div>
      </section>

      {/* Banner B */}
      <BannerBand image={BANNER2_IMAGE} objectPos="center 38%" align="right" testid="banner-2" />

      {/* Registry */}
      <section id="registry" className="py-28 sm:py-36 px-6 sm:px-12 bg-espresso" data-testid="registry-section">
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center mb-16">
            <Label>Registry</Label>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl text-ivory font-light">Gifts &amp; Well Wishes</h2>
            <p className="mt-6 text-cream/75 max-w-xl mx-auto leading-relaxed">
              Your presence is the greatest gift. If you wish to celebrate us with something more, we have shared a couple of options below.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-sm border border-gold-soft bg-espresso-dark p-9 flex flex-col transition-transform duration-500 hover:-translate-y-1">
                <Gift className="w-8 h-8 text-gold mb-6" strokeWidth={1.3} />
                <h3 className="font-display text-2xl text-ivory mb-2">Amazon Registry</h3>
                <p className="text-cream/75 text-sm flex-1 leading-relaxed">Browse our registry for gifts to help us build our new home together.</p>
                <a href={c.amazon_registry_url} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gold text-espresso-dark tracking-[0.12em] uppercase text-sm hover:bg-ivory transition-colors" data-testid="amazon-registry-btn">
                  View Amazon Registry
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-sm border border-gold-soft bg-espresso-dark p-9 flex flex-col transition-transform duration-500 hover:-translate-y-1">
                <Heart className="w-8 h-8 text-gold mb-6" strokeWidth={1.3} />
                <h3 className="font-display text-2xl text-ivory mb-2">Honeymoon Fund</h3>
                <p className="text-cream/75 text-sm flex-1 leading-relaxed">A contribution toward our honeymoon adventure through The Knot.</p>
                {c.knot_available ? (
                  <a href={c.knot_registry_url} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gold text-espresso-dark tracking-[0.12em] uppercase text-sm hover:bg-ivory transition-colors" data-testid="knot-registry-btn">
                    Contribute on The Knot
                  </a>
                ) : (
                  <div className="mt-7 inline-flex items-center justify-center px-6 py-3 rounded-full border border-gold-soft text-cream/55 italic text-sm" data-testid="knot-coming-soon">
                    Honeymoon fund coming soon
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-28 sm:py-36 px-6 sm:px-12 bg-espresso-dark" data-testid="contact-section">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-12">
            <Label>Questions?</Label>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl text-ivory font-light">Get in Touch</h2>
            <p className="mt-5 text-cream/70">For anything at all, reach out anytime. On the wedding day, Leah is your go to.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid sm:grid-cols-2 gap-5">
              {(c.contacts || []).map((ct) => (
                <div key={ct.name} className="rounded-sm border border-gold-soft bg-espresso p-7 text-center">
                  <p className="text-[11px] tracking-luxe uppercase text-gold">{ct.label}</p>
                  <p className="font-display text-2xl text-ivory mt-2">{ct.name}</p>
                  <div className="mt-5 flex justify-center gap-3">
                    <a href={`tel:${ct.phone.replace(/[^0-9]/g, "")}`} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gold text-espresso-dark text-sm hover:bg-ivory transition-colors" data-testid={`call-${ct.name.split(" ")[0].toLowerCase()}`}>
                      <Phone className="w-4 h-4" /> Call
                    </a>
                    <a href={`sms:${ct.phone.replace(/[^0-9]/g, "")}`} className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-gold text-gold text-sm hover:bg-gold hover:text-espresso-dark transition-colors" data-testid={`text-${ct.name.split(" ")[0].toLowerCase()}`}>
                      <MessageSquare className="w-4 h-4" /> Text
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* RSVP CTA with artsy background */}
      <section className="relative py-32 sm:py-44 px-6 text-center overflow-hidden" data-testid="rsvp-cta">
        <img src={CTA_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center sm:object-[center_25%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso-dark/85 via-espresso-dark/70 to-espresso-dark/90" />
        <Reveal className="relative max-w-2xl mx-auto">
          <Label>Will you join us?</Label>
          <h2 className="mt-5 font-display text-5xl sm:text-6xl text-ivory font-light">Kindly Respond</h2>
          <p className="mt-6 text-cream/85">Please respond by {c.rsvp_deadline_display} using the personal link from your invitation.</p>
          <Link to="/rsvp" className="mt-9 inline-block px-10 py-4 rounded-full bg-gold text-espresso-dark tracking-[0.14em] uppercase text-sm hover:bg-ivory transition-colors" data-testid="cta-rsvp-button">
            RSVP Now
          </Link>
        </Reveal>
      </section>

      <footer className="py-14 px-6 bg-espresso-dark border-t border-gold-soft text-center">
        <p className="font-display text-4xl text-ivory">Angeo <span className="italic font-serif" style={{ color: HERO_BROWN }}>&amp;</span> Gracie</p>
        <p className="mt-3 text-cream/50 text-sm tracking-luxe uppercase">November 8, 2026 · Tahlequah, Oklahoma</p>
      </footer>
    </div>
  );
}

function Countdown() {
  const target = Date.UTC(2026, 10, 8, 22, 30, 0); // Nov 8, 2026, 4:30 PM CST (UTC-6)
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const units = [[d, "Days"], [h, "Hours"], [m, "Minutes"], [s, "Seconds"]];
  return (
    <div className="mt-7 flex gap-5 sm:gap-8" data-testid="countdown">
      {units.map(([v, l]) => (
        <div key={l} className="flex flex-col items-center">
          <span className="font-display text-3xl sm:text-5xl text-white tabular-nums" style={{ textShadow: "0 2px 20px rgba(20,12,8,0.65)" }}>
            {String(v).padStart(2, "0")}
          </span>
          <span className="text-[9px] sm:text-[10px] tracking-luxe uppercase text-white/70 mt-1.5">{l}</span>
        </div>
      ))}
    </div>
  );
}

function BannerBand({ image, objectPos = "center", align = "right", eyebrow, quote, children, testid, imgClassName }) {
  const reduce = useReducedMotion();
  const alignCls = align === "right" ? "lg:items-end lg:text-right" : "lg:items-start lg:text-left";
  const sideGrad = align === "right"
    ? "bg-gradient-to-l from-espresso-dark/90 via-espresso-dark/50 to-espresso-dark/15"
    : "bg-gradient-to-r from-espresso-dark/90 via-espresso-dark/50 to-espresso-dark/15";
  return (
    <section className="relative h-[62vh] sm:h-[78vh] w-full overflow-hidden bg-espresso-dark" data-testid={testid}>
      {imgClassName ? (
        <img
          src={image} alt="" aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover ${imgClassName}`}
          loading="lazy"
        />
      ) : (
        <motion.img
          src={image} alt="" aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: objectPos }}
          initial={reduce ? false : { scale: 1.1 }}
          whileInView={reduce ? false : { scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 8, ease: "linear" }}
          loading="lazy"
        />
      )}
      <div className={`absolute inset-0 ${sideGrad}`} />
      <div className="absolute inset-0 bg-gradient-to-t from-espresso-dark/55 to-transparent" />
      <div className={`relative h-full flex flex-col items-center justify-center text-center px-8 lg:px-24 ${alignCls}`}>
        {eyebrow ? <Label>{eyebrow}</Label> : null}
        {quote ? (
          <p className="mt-4 font-script text-white text-5xl sm:text-7xl leading-[1.05]" style={{ textShadow: "0 2px 26px rgba(20,12,8,0.65)" }}>{quote}</p>
        ) : null}
        {children}
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, title, body, testid }) {
  return (
    <Reveal>
      <div className="h-full rounded-sm border border-gold-soft bg-espresso p-8 transition-transform duration-500 hover:-translate-y-1" data-testid={testid}>
        <Icon className="w-6 h-6 text-gold mb-4" strokeWidth={1.3} />
        <h3 className="font-display text-xl text-ivory mb-2">{title}</h3>
        <p className="text-cream/80 text-sm leading-relaxed">{body}</p>
      </div>
    </Reveal>
  );
}

function PhotoFrame({ label, className = "" }) {
  return (
    <Reveal className={className}>
      <div className="w-full h-full relative overflow-hidden rounded-sm border border-gold-soft bg-espresso flex flex-col items-center justify-center text-cream/35">
        <Camera className="w-8 h-8 mb-3" strokeWidth={1.1} />
        <span className="text-[10px] tracking-luxe uppercase">{label}</span>
      </div>
    </Reveal>
  );
}
